import Phaser from "phaser";
import { DirectionalText, NineSliceButton, NINE_SLICE_BUTTON_EVENT } from "@yjsgames/phaser-ui";
import type { TextDirection } from "@yjsgames/i18n";
import { applyTextResolution, FONT_FAMILY, SMALL_BUTTON_STYLE, setButtonEnabled, TYPO } from "./uiTokens.js";
import { PALETTE } from "../themes/palette.js";
import { t, getTextDirection } from "../i18n/i18n.js";
import { tipsForTab, type TipDef } from "../data/tipCatalog.js";
import { randomStartIndex, stepTipIndex } from "./tipCarousel.js";

/** 화살표(◀/▶) 버튼 폭 — 한 글자라 SMALL_BUTTON_STYLE(56)보다 좁게 override. */
const ARROW_WIDTH = 28;
/** 화살표 버튼과 본문 사이 가로 여백. */
const ARROW_GAP = 8;

/**
 * 탭뷰 팁 캐러셀 — 제목 아래에 짧은 팁/설명(2~3줄)을 표시하고 `◀`/`▶`로 순환한다(진입 시 랜덤 시작).
 * **재사용 컴포넌트** — `tab` 인자만 바꿔 어느 탭뷰에도 붙일 수 있다(본문은 tips.tsv `tab` 컬럼으로 분류, i18n 키 분리).
 *
 * 인덱스 결정(랜덤 시작·순환)은 순수 모듈 tipCarousel.ts(단위테스트)에 위임하고, 본 View는 Phaser 연결만 담당한다.
 * 좌표는 컨테이너 로컬(0..width / 0..height): 화살표는 좌/우 끝 고정, 본문은 가운데 wordWrap(화살표 침범 차단).
 * 팁이 1개 이하면 화살표를 비활성(setButtonEnabled)하고, 0개면 본문도 빈 문자열로 둔다.
 */
export class TipCarouselView extends Phaser.GameObjects.Container {
  private readonly tips: readonly TipDef[];
  private readonly tipText: DirectionalText;
  private readonly prevBtn: NineSliceButton;
  private readonly nextBtn: NineSliceButton;
  private readonly arrowsHidden: boolean;
  private index: number;

  constructor(
    scene: Phaser.Scene,
    x: number,
    y: number,
    width: number,
    height: number,
    tab: string,
    rng: () => number = Math.random,
    opts?: { showArrows?: boolean },
  ) {
    super(scene, x, y);
    this.tips = tipsForTab(tab);
    this.index = randomStartIndex(this.tips.length, rng);

    const wrapWidth = Math.max(48, width - 2 * (ARROW_WIDTH + ARROW_GAP));
    const btnH = Math.min(height, 26);

    const tipText = new DirectionalText(scene, width / 2, height / 2, this.currentText(), {
      dir: getTextDirection(),
      align: "center",
      applyBidi: true,
      style: {
        fontFamily: FONT_FAMILY,
        fontSize: TYPO.caption,
        color: PALETTE.textSecondary,
        align: "center",
        wordWrap: { width: wrapWidth, useAdvancedWrap: true },
      },
    });
    this.tipText = applyTextResolution(tipText);

    this.prevBtn = new NineSliceButton(scene, ARROW_WIDTH / 2, height / 2, {
      style: SMALL_BUTTON_STYLE,
      label: "◀",
      widthOverride: ARROW_WIDTH,
      heightOverride: btnH,
    });
    this.nextBtn = new NineSliceButton(scene, width - ARROW_WIDTH / 2, height / 2, {
      style: SMALL_BUTTON_STYLE,
      label: "▶",
      widthOverride: ARROW_WIDTH,
      heightOverride: btnH,
    });
    this.prevBtn.on(NINE_SLICE_BUTTON_EVENT.Click, () => this.step(-1));
    this.nextBtn.on(NINE_SLICE_BUTTON_EVENT.Click, () => this.step(1));

    this.arrowsHidden = opts?.showArrows === false;
    this.add([this.tipText, this.prevBtn, this.nextBtn]);
    if (this.arrowsHidden) {
      this.prevBtn.setVisible(false);
      this.nextBtn.setVisible(false);
    }
    this.refreshArrows();
  }

  /** 현재 인덱스의 본문 — 팁 없으면 빈 문자열. */
  private currentText(): string {
    const tip = this.tips[this.index];
    return tip ? t(tip.messageKey) : "";
  }

  /** ◀/▶ — 순환 이동 후 본문 갱신(tipCarousel 순수 로직). */
  private step(dir: 1 | -1): void {
    if (this.tips.length <= 1) return;
    this.index = stepTipIndex(this.index, this.tips.length, dir);
    this.tipText.setLogicalText(this.currentText());
  }

  /** 팁 1개 이하면 화살표 비활성, 탭에서 숨김 요청을 하면 버튼 자체를 숨긴다. */
  private refreshArrows(): void {
    const enabled = !this.arrowsHidden && this.tips.length > 1;
    setButtonEnabled(this.prevBtn, enabled);
    setButtonEnabled(this.nextBtn, enabled);
    if (this.arrowsHidden) {
      this.prevBtn.setVisible(false);
      this.nextBtn.setVisible(false);
    }
  }

  /** 언어 변경 — 현재 팁 본문을 새 언어로 재조회(화살표 글리프는 방향 무관 정적). */
  applyLanguage(): void {
    this.tipText.setLogicalText(this.currentText());
  }

  override setVisible(visible: boolean): this {
    super.setVisible(visible);
    (this as Phaser.GameObjects.GameObject & { setActive?: (value: boolean) => unknown }).setActive?.(
      visible,
    );
    this.tipText.setVisible(visible);
    this.prevBtn.setVisible(visible && !this.arrowsHidden);
    this.nextBtn.setVisible(visible && !this.arrowsHidden);
    return this;
  }

  /** 방향 전환(RTL) — 본문 정렬/Bidi 재배열. 화살표는 보편적 prev/next라 위치 고정. */
  relayout(dir: TextDirection): void {
    this.tipText.setDirection(dir);
  }
}
